import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserGroup, UserGroupDto } from 'src/app/model/UserGroup';
import { GroupService } from 'src/app/services/group/group.service';
import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs';
import { PermissionEntry } from 'src/app/model/User';
import { setEquals } from 'src/app/shared/array-equals';

class PermissionWrapper extends PermissionEntry {
    private _enabled: boolean;

    constructor(permission: string, companyId: number, enabled: boolean) {
        super(null, companyId, permission);
        this._enabled = enabled;
    }

    get enabled(): boolean {
        return this._enabled;
    }

    set enabled(value: boolean) {
        this._enabled = value;
    }
}

export interface EditGroupDialogData {
    group: UserGroup;
}

/**
 * Dialog component for editing group permissions
 * Allows editing group name and feature permissions
 */
@Component({
    selector: 'app-edit-group-dialog',
    templateUrl: './edit-group-dialog.component.html',
    styleUrls: ['./edit-group-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditGroupDialogComponent implements OnInit {
    /** Form group for group editing */
    public groupForm!: FormGroup;

    /** Edited group object properties */
    public editedGroup!: UserGroup;

    /** Original group object properties to track changes */
    private _originalGroup!: UserGroup;

    /** List of all permissions with their enabled state */
    public existingPermissions: PermissionWrapper[] = [];

    /** List of all existing groups for name validation */
    private _existingGroups: UserGroup[] = [];

    /** Status on if an action button was clicked */
    public doingAction: boolean = false;

    /** Status on if permissions were changed */
    public permissionsChanged: boolean = false;

    /**
     * Default constructor to initialize the dialog component
     * 
     * @param data 
     * @param dialogRef 
     * @param _groupService 
     * @param _toastr 
     */
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: EditGroupDialogData,
        public dialogRef: MatDialogRef<EditGroupDialogComponent>,
        private _formBuilder: FormBuilder,
        private _groupService: GroupService,
        private _toastr: ToastrService,
    ) { }

    /**
     * Initialize the component when the dialog is opened
     */
    ngOnInit(): void {
        // Initialize edited group with current values and original values for comparison
        this.editedGroup = this.data.group.copyOf();
        this._originalGroup = this.data.group.copyOf();

        // Load existing groups for validation
        this._existingGroups = this._groupService.getEntities() || [];

        // Load a list of all permissions
        this._groupService.getAllPermissions().pipe(tap((permissions: PermissionEntry[]) => {
            // Map permissions to PermissionWrapper with enabled state
            this.existingPermissions = permissions.map(p => {
                return new PermissionWrapper(p.permission, p.companyId, this.editedGroup.permissions.some(ep => ep.permission === p.permission));
            });
        })).subscribe();

        // Initialize group editor form with validators
        this.groupForm = this._formBuilder.group({
            name: [
                this.editedGroup.name,
                [
                    Validators.required,
                    this._alphanumericSpaceValidator,
                    this._uniqueNameValidator.bind(this)
                ]
            ]
        });

        // Update editedGroup when form value changes
        this.groupForm.get('name')?.valueChanges.subscribe(value => {
            if (value !== null && value !== undefined) {
                this.editedGroup.name = value;
            }
        });
    }

    /**
     * Custom validator for alphanumeric characters and spaces only
     */
    private _alphanumericSpaceValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }

        const alphanumericSpaceRegex = /^[a-zA-Z0-9 ]+$/;
        if (!alphanumericSpaceRegex.test(control.value)) {
            return { alphanumericSpace: { value: control.value } };
        }

        return null;
    }

    /**
     * Custom validator to ensure group name is unique
     */
    private _uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Let required validator handle empty values
        }

        // Don't validate against the original name (allow keeping the same name)
        if (control.value === this._originalGroup.name) {
            return null;
        }

        const nameExists = this._existingGroups.some(group => 
            group.name.toLowerCase() === control.value.toLowerCase()
        );

        if (nameExists) {
            return { uniqueName: { value: control.value } };
        }

        return null;
    }

    /**
     * Check if the form is valid
     */
    public isFormValid(): boolean {
        return this.groupForm.valid;
    }

    /**
     * Handle permission toggle
     */
    public onPermissionToggle(permission: PermissionWrapper): void {
        // Toggle enabled state
        this.existingPermissions.map(p => {
            if (p.permission === permission.permission) {
                p.enabled = !p.enabled;
            }
        });

        if (!permission.enabled) {
            // If we unchecked a permission, ensure it is removed from the edited group
            this.editedGroup.permissions = this.editedGroup.permissions.filter(p => p.permission !== permission.permission);
        } else {
            // Otherwise, add it to the edited group
            this.editedGroup.permissions.push(new PermissionEntry(null, permission.companyId, permission.permission));
        }

        // Edit permissions changed status
        const originalSet = new Set(this._originalGroup.permissions.map(p => p.permission));
        const editedSet = new Set(this.editedGroup.permissions.map(p => p.permission));

        this.permissionsChanged = !setEquals(originalSet, editedSet);
    }

    /**
     * Save the group changes
     */
    public save(): void {
        console.log("[EDIT-GROUP-DIALOG]: Saving group changes", this.editedGroup);
        
        // Validate form before saving
        if (!this.isFormValid()) {
            console.log("[EDIT-GROUP-DIALOG]: Form is invalid, cannot save");
            this._toastr.error('Please fix validation errors before saving');
            return;
        }

        this.doingAction = true;

        // Check if there are unsaved changes before proceeding
        if (!this.groupForm.dirty && !this.permissionsChanged) {
            console.log("[EDIT-GROUP-DIALOG]: No changes to save");
            this.doingAction = false;
            return;
        }

        // Update the group
        this._groupService.updateGroup(this.editedGroup).subscribe({
            next: (g: UserGroupDto) => {
                console.log("[EDIT-GROUP-DIALOG]: Group updated successfully", g);
                this._toastr.success('Group updated successfully');
                this.doingAction = false;
                this.dialogRef.close({ action: 'save', data: g });
            },
            error: (error: any) => {
                console.error("[EDIT-GROUP-DIALOG]: Error updating group", error);
                this.doingAction = false;
                this._toastr.error('Failed to update group');
            },
        });
    }

    /**
     * Close the dialog without saving
     */
    public cancel(): void {
        this.dialogRef.close();
    }
}
